import { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Loader2, ZoomIn } from "lucide-react";

async function getCroppedBase64(imageSrc, cropPixels, size = 640) {
  const image = await new Promise((res, rej) => {
    const img = new Image();
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = imageSrc;
  });
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(
    image,
    cropPixels.x, cropPixels.y, cropPixels.width, cropPixels.height,
    0, 0, size, size
  );
  return canvas.toDataURL("image/jpeg", 0.85);
}

export const PhotoCropDialog = ({ open, image, onCancel, onCropped }) => {
  const { t } = useTranslation();
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixels, setPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  const onComplete = useCallback((_area, areaPixels) => setPixels(areaPixels), []);

  const reset = () => { setCrop({ x: 0, y: 0 }); setZoom(1); setPixels(null); };

  const handleSave = async () => {
    if (!pixels) return;
    setBusy(true);
    try {
      const b64 = await getCroppedBase64(image, pixels);
      reset();
      onCropped(b64);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { reset(); onCancel(); } }}>
      <DialogContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="max-w-md overflow-hidden rounded-3xl border-white/10 bg-[#141110] p-0"
        data-testid="photo-crop-dialog"
      >
        <div className="px-5 pt-5 text-center">
          <DialogTitle className="text-lg font-semibold text-white">{t("cropper.title")}</DialogTitle>
          <DialogDescription className="mt-1 text-xs text-muted-foreground">{t("cropper.hint")}</DialogDescription>
        </div>
        <div className="relative mt-4 h-72 w-full bg-black">
          {image && (
            <Cropper
              image={image}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              minZoom={1}
              maxZoom={4}
              restrictPosition={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onComplete}
            />
          )}
        </div>
        <div className="flex items-center gap-3 px-5 py-4">
          <ZoomIn size={18} className="shrink-0 text-amber-300" />
          <Slider
            min={1}
            max={4}
            step={0.01}
            value={[zoom]}
            onValueChange={(v) => setZoom(v[0])}
            className="flex-1"
            data-testid="crop-zoom-slider"
            aria-label={t("cropper.zoom")}
          />
        </div>
        <div className="flex gap-3 px-5 pb-5">
          <Button variant="outline" onClick={() => { reset(); onCancel(); }} className="flex-1 rounded-full" data-testid="crop-cancel-btn">
            {t("cropper.cancel")}
          </Button>
          <Button onClick={handleSave} disabled={busy || !pixels} className="flex-1 rounded-full bg-sky-500 hover:bg-sky-400" data-testid="crop-save-btn">
            {busy ? <Loader2 className="animate-spin" size={16} /> : t("cropper.save")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PhotoCropDialog;
